<template>
  <nav
    class="m-stepper"
    :class="[`m-stepper--${orientation}`, { 'm-stepper--disabled': disabled }]"
    :aria-label="label || undefined"
    :aria-orientation="orientation"
  >
    <ol class="m-stepper__list">
      <li
        v-for="(step, index) in normalizedSteps"
        :key="String(step.stepId)"
        class="m-stepper__item"
        :class="`m-stepper__item--${stepStatus(index)}`"
      >
        <div
          class="m-stepper__node"
          :aria-current="stepStatus(index) === 'active' ? 'step' : undefined"
        >
          <span class="m-stepper__marker">
            <i v-if="stepStatus(index) === 'done'" class="pi pi-check" aria-hidden="true" />
            <span v-else>{{ index + 1 }}</span>
          </span>
          <span
            v-if="index < normalizedSteps.length - 1"
            class="m-stepper__connector"
            aria-hidden="true"
          />
        </div>
        <span class="m-stepper__label">{{ step.stepDescription }}</span>
      </li>
    </ol>
    <div v-if="hint" class="m-stepper__hint">{{ hint }}</div>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  normalizeProgressStepperRows,
  type ProgressStepperRow,
} from '@/common/utils/progressStepper'

interface Props {
  steps?: unknown
  label?: string
  hint?: string
  orientation?: 'horizontal' | 'vertical'
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  steps: () => [],
  label: '',
  hint: '',
  orientation: 'horizontal',
  disabled: false,
})

const normalizedSteps = computed<ProgressStepperRow[]>(() =>
  normalizeProgressStepperRows(props.steps),
)
const currentStep = computed(() => normalizedSteps.value.findIndex((step) => !step.stepStatus))

const stepStatus = (index: number): 'done' | 'active' | 'pending' => {
  if (normalizedSteps.value[index]?.stepStatus) return 'done'
  if (index === (currentStep.value < 0 ? normalizedSteps.value.length : currentStep.value))
    return 'active'
  return 'pending'
}
</script>

<style scoped lang="scss">
.m-stepper {
  --stepper-ink: #111111;
  --stepper-accent: #ffc400;
  --stepper-muted: #d9d9d9;
  --stepper-text: #6b6b6b;
  width: 100%;
  color: var(--stepper-text);

  &--disabled {
    opacity: 0.6;
  }

  &__list {
    display: flex;
    align-items: flex-start;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__item {
    display: flex;
    min-width: 0;
    flex: 1 1 0;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  &__node {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  &__marker {
    z-index: 1;
    display: inline-flex;
    flex: 0 0 2rem;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border: 2px solid var(--stepper-muted);
    border-radius: 50%;
    background: var(--surface-card, #fff);
    color: #64748b;
    font-size: 0.8rem;
    font-weight: 700;
    transition: all 180ms ease;
  }

  &__connector {
    position: absolute;
    top: 50%;
    left: calc(50% + 1rem + 0.45rem);
    width: calc(100% - 2rem - 0.9rem);
    height: 2px;
    margin: 0;
    background: var(--stepper-muted);
    transform: translateY(-50%);
    transition: background 180ms ease;
  }

  &__label {
    width: 100%;
    margin-top: 0.5rem;
    padding: 0 0.35rem;
    font-size: 0.8rem;
    line-height: 1.3;
    text-align: center;
    overflow-wrap: break-word;
  }

  &__item--done {
    .m-stepper__marker {
      border-color: var(--stepper-ink);
      background: var(--stepper-ink);
      color: var(--stepper-accent);
    }

    .m-stepper__connector {
      background: var(--stepper-ink);
    }

    .m-stepper__label {
      color: var(--stepper-ink);
    }
  }

  &__item--active {
    .m-stepper__marker {
      border-color: var(--stepper-ink);
      background: var(--stepper-accent);
      box-shadow: 0 0 0 4px color-mix(in srgb, var(--stepper-accent) 42%, transparent);
      color: var(--stepper-ink);
    }

    .m-stepper__label {
      color: var(--stepper-ink);
      font-weight: 700;
    }
  }

  &__hint {
    margin-top: 0.75rem;
    color: #64748b;
    font-size: 0.75rem;
  }

  &--vertical {
    .m-stepper__list {
      flex-direction: column;
      gap: 0;
    }

    .m-stepper__item {
      display: grid;
      grid-template-columns: 2rem 1fr;
      flex: none;
      align-items: start;
      column-gap: 0.75rem;
      text-align: left;
    }

    .m-stepper__node {
      position: relative;
      width: 2rem;
      height: 3.25rem;
      flex-direction: column;
      justify-content: flex-start;
    }

    .m-stepper__connector {
      position: static;
      top: auto;
      left: auto;
      width: 2px;
      height: 1.25rem;
      margin: 0.35rem 0;
      transform: none;
    }

    .m-stepper__label {
      width: auto;
      max-width: none;
      margin-top: 0.45rem;
      padding: 0;
      text-align: left;
    }
  }
}
</style>
